import { useMemo } from "react";
import { ausIso, fruehabholung } from "@mrmachine/fach";
import type { Randregel } from "@mrmachine/fach";
import { useT } from "../i18n";
import { useSitzung } from "../schale/Sitzung";
import { tagImBetrieb } from "../daten/betriebstag";
import { tagMitWochentag } from "./preistext";
import "../stile/randtag.css";

/* ══════════════════════════════════════════════════════════════════
   Abholung am Vorabend, Rückgabe am Morgen — der leise Hinweis
   (Migration 0286, 08.10.2026).

   Hat die Regel des Betriebs gegriffen, steht am Protokoll der Tag, ab
   bzw. bis zu dem berechnet wird (`berechnetIso`). Wer den Auftrag, das
   Übergabeprotokoll oder den Laufzettel liest, soll sehen, warum die
   Rechnung einen Tag später beginnt bzw. früher endet als das Protokoll —
   ohne dass es wie ein offener Punkt aussieht. Darum leise, kein Band.

   Am Telefon steht derselbe Satz vor der Unterschrift, dort in der Form
   „wird nicht berechnet" (`masken/HandyProtokoll.tsx`).
   ══════════════════════════════════════════════════════════════════ */

/** Was der Hinweis braucht — ein Protokoll mit Art und berechnetem Tag. */
export type Randtagquelle = {
  art: "ausgabe" | "ruecknahme";
  berechnetIso?: string | undefined;
  ressource?: string;
  spaet?: { mietende: string; berechnetBis: string; geplant: number; berechnet: number } | undefined;
};

/** Die Protokolle, an denen die Regel gegriffen hat. */
export function randtagProtokolle<P extends Randtagquelle>(liste: readonly P[]): P[] {
  return liste.filter((p) => Boolean(p.berechnetIso));
}

/** Die Rücknahmen, die nach dem eingetragenen Mietende lagen. */
export function spaeteProtokolle<P extends Randtagquelle>(liste: readonly P[]): P[] {
  return liste.filter((p) => p.art === "ruecknahme" && Boolean(p.spaet));
}

/** Der Satz: „Abholung am Vorabend — berechnet ab Fr 09.10." */
export function Randtagsatz({ art, tag }: { art: "ausgabe" | "ruecknahme"; tag: string }) {
  const t = useT();
  return (
    <span className="leise randtag">
      {t(art === "ausgabe" ? "vorgang.randtage.vorabend" : "vorgang.randtage.morgen", {
        tag: tagMitWochentag(t, tag),
      })}
    </span>
  );
}

/**
 * Alle Hinweise eines Auftrags untereinander. Bei mehreren Geräten mit dem
 * Gerät davor — „HH-23: Abholung am Vorabend …".
 */
export function Randtaghinweise({ liste, mitGeraet = false }: { liste: readonly Randtagquelle[]; mitGeraet?: boolean }) {
  const t = useT();
  const gegriffen = randtagProtokolle(liste);
  const spaete = spaeteProtokolle(liste);
  if (!gegriffen.length && !spaete.length) return null;
  return (
    <span className="randtage">
      {/* Später zurück als vereinbart (Lisas Interview, 08.10.2026): kein
          leiser Satz, sondern ein Hinweis — die Rechnung wird länger als der
          Auftrag, und das soll man sehen, bevor man freigibt. */}
      {spaete.map((p, i) => {
        const sp = p.spaet as NonNullable<Randtagquelle["spaet"]>;
        const satz = t("vorgang.randtage.spaet", {
          mietende: tagMitWochentag(t, sp.mietende),
          bis: tagMitWochentag(t, sp.berechnetBis),
          berechnet: sp.berechnet,
          geplant: sp.geplant,
        });
        return (
          <span key={`spaet-${sp.berechnetBis}-${i}`} className="randtag randtag-spaet" role="note">
            {mitGeraet && p.ressource ? t("vorgang.randtage.mitGeraet", { geraet: p.ressource, satz }) : satz}
          </span>
        );
      })}
      {gegriffen.map((p, i) => (
        <span key={`${p.art}-${p.berechnetIso}-${i}`} className="leise randtag">
          {(() => {
            const satz = t(p.art === "ausgabe" ? "vorgang.randtage.vorabend" : "vorgang.randtage.morgen", {
              tag: tagMitWochentag(t, p.berechnetIso as string),
            });
            return mitGeraet && p.ressource ? t("vorgang.randtage.mitGeraet", { geraet: p.ressource, satz }) : satz;
          })()}
        </span>
      ))}
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Abholung schon vor dem Wochenende — der Hinweis beim Vermieten
   (Lisas Interview, Frage 2, 08.10.2026).

   Die eingetragene Mietdauer entscheidet über das Wochenende: Mietbeginn
   Montag, Samstag und Sonntag ohne Abholung — dann kann der Kunde am Freitag
   ab der Vorabend-Uhrzeit holen, und Freitag bis Sonntag kosten nichts. Wer
   die Mietdauer einträgt, soll das sehen, bevor er sie bestätigt. Die
   Abholzeiten kommen aus `GET /ich` (jeder, der vermietet); ohne „Kein
   Abholtag" in den Einstellungen erscheint nichts.
   ══════════════════════════════════════════════════════════════════ */

/** Die Abholzeiten des Betriebs — ohne Sitzung (Baustein im Test) keine. */
function useAbholregel(): Randregel | null {
  try {
    return useSitzung().ich?.abholung ?? null;
  } catch {
    return null;
  }
}

export function Fruehabholungshinweis({ von, klasse = "" }: { von: string | null | undefined; klasse?: string }) {
  const t = useT();
  const regel = useAbholregel();
  const frueh = useMemo(() => {
    const iso = von ? tagImBetrieb(von) : "";
    return regel && iso ? fruehabholung(ausIso(iso), regel) : null;
  }, [von, regel]);
  if (!frueh) return null;
  return (
    <span className={`randtag-frueh${klasse ? ` ${klasse}` : ""}`}>
      {t("vorgang.randtage.frueh", {
        tag: tagMitWochentag(t, frueh.tag),
        uhr: frueh.ab,
        beginn: tagMitWochentag(t, frueh.beginn),
      })}
    </span>
  );
}
